// GENERATED by scripts/generate-schemas.ts -- do not edit.
//
// The Drizzle schema is the source of truth (ADR-0008). This file is its
// serialised form, imported by clients so they do not pull the ORM into their
// bundle (ADR-0022).
//
// To change anything here, change packages/db/src/schema.ts or
// packages/shared/src/derived.ts and run:
//
//     pnpm --filter @tabletop/shared generate
//
// The gate runs that and fails on a dirty tree, so an un-regenerated change
// cannot merge.

import { z } from "zod";

export const slugSchema = z.string().min(1).max(200).regex(/^[a-z0-9-]+$/);

export const userSchema = z.object({
  "id": z.uuid(),
  "clerkUserId": z.string(),
  "displayName": z.string(),
  "createdAt": z.date(),
  "updatedAt": z.date(),
  "deletedAt": z.date().nullable(),
});

export const newUserSchema = z.object({
  "clerkUserId": z.string(),
  "displayName": z.string().min(1).max(120),
});

export const gameSchema = z.object({
  "id": z.uuid(),
  "slug": z.string(),
  "title": z.string(),
  "description": z.string().nullable(),
  "yearPublished": z.number().min(-2147483648).max(2147483647).nullable(),
  "designers": z.array(z.string()),
  "publishers": z.array(z.string()),
  "mechanics": z.array(z.string()),
  "themes": z.array(z.string()),
  "minPlayers": z.number().min(-2147483648).max(2147483647).nullable(),
  "maxPlayers": z.number().min(-2147483648).max(2147483647).nullable(),
  "playingTimeMinutes": z.number().min(-2147483648).max(2147483647).nullable(),
  "weight": z.string().nullable(),
  "imageUrl": z.string().nullable(),
  "bggId": z.number().min(-2147483648).max(2147483647).nullable(),
  "wikidataId": z.string().nullable(),
  "createdAt": z.date(),
  "updatedAt": z.date(),
  "searchVector": z.any().nullable(),
});

export const newGameSchema = z.object({
  "slug": z.string().min(1).regex(/^[a-z0-9-]+$/),
  "title": z.string().min(1).max(300),
  "description": z.string().nullable().optional(),
  "yearPublished": z.number().min(-2147483648).max(2147483647).int().min(1000).max(2200).nullable().optional(),
  "designers": z.array(z.string()).optional(),
  "publishers": z.array(z.string()).optional(),
  "mechanics": z.array(z.string()).optional(),
  "themes": z.array(z.string()).optional(),
  "minPlayers": z.number().min(-2147483648).max(2147483647).int().min(1).nullable().optional(),
  "maxPlayers": z.number().min(-2147483648).max(2147483647).int().min(1).nullable().optional(),
  "playingTimeMinutes": z.number().min(-2147483648).max(2147483647).nullable().optional(),
  "weight": z.string().nullable().optional(),
  "imageUrl": z.string().nullable().optional(),
  "bggId": z.number().min(-2147483648).max(2147483647).nullable().optional(),
  "wikidataId": z.string().nullable().optional(),
});

export const gameProvenanceSchema = z.object({
  "id": z.uuid(),
  "gameId": z.uuid(),
  "field": z.string(),
  "source": z.string(),
  "sourceUrl": z.string().nullable(),
  "licence": z.string(),
  "confidence": z.string().nullable(),
  "model": z.string().nullable(),
  "author": z.string().nullable(),
  "fetchedAt": z.date(),
});

export const newGameProvenanceSchema = z.object({
  "gameId": z.uuid(),
  "field": z.string(),
  "source": z.string(),
  "sourceUrl": z.string().nullable().optional(),
  "licence": z.string(),
  "confidence": z.string().nullable().optional(),
  "model": z.string().nullable().optional(),
  "author": z.string().nullable().optional(),
  "fetchedAt": z.date().optional(),
});

export const playerSchema = z.object({
  "id": z.uuid(),
  "createdByUserId": z.uuid(),
  "userId": z.uuid().nullable(),
  "displayName": z.string(),
  "claimedAt": z.date().nullable(),
  "createdAt": z.date(),
});

export const newPlayerSchema = z.object({
  "createdByUserId": z.uuid(),
  "userId": z.uuid().nullable().optional(),
  "displayName": z.string().min(1).max(120),
});

export const shelfEntrySchema = z.object({
  "id": z.uuid(),
  "userId": z.uuid(),
  "gameId": z.uuid(),
  "status": z.string(),
  "rating": z.number().min(-32768).max(32767).nullable(),
  "review": z.string().nullable(),
  "createdAt": z.date(),
  "updatedAt": z.date(),
});

export const newShelfEntrySchema = z.object({
  "userId": z.uuid(),
  "gameId": z.uuid(),
  "status": z.string().optional(),
});

export const sessionSchema = z.object({
  "id": z.uuid(),
  "gameId": z.uuid(),
  "loggedByUserId": z.uuid(),
  "playedAt": z.date(),
  "rating": z.number().min(-32768).max(32767).nullable(),
  "review": z.string().nullable(),
  "notes": z.string().nullable(),
  "tags": z.array(z.string()),
  "teachMinutes": z.number().min(-2147483648).max(2147483647).nullable(),
  "wasTaught": z.boolean().nullable(),
  "durationMinutes": z.number().min(-2147483648).max(2147483647).nullable(),
  "location": z.string().nullable(),
  "variant": z.string().nullable(),
  "expansions": z.array(z.string()),
  "createdAt": z.date(),
});

export const newSessionSchema = z.object({
  "gameId": z.uuid(),
  "loggedByUserId": z.uuid(),
  "playedAt": z.date(),
  "rating": z.number().int().min(1).max(10).optional().nullable().nullable().optional(),
  "review": z.string().nullable().optional(),
  "notes": z.string().nullable().optional(),
  "tags": z.array(z.string().min(1).max(40)).max(20).optional().optional(),
  "teachMinutes": z.number().int().min(1).max(600).optional().nullable().nullable().optional(),
  "wasTaught": z.boolean().nullable().optional(),
  "durationMinutes": z.number().min(-2147483648).max(2147483647).int().min(1).optional().nullable().nullable().optional(),
  "location": z.string().nullable().optional(),
  "variant": z.string().nullable().optional(),
  "expansions": z.array(z.string()).optional(),
});

export const sessionParticipantSchema = z.object({
  "id": z.uuid(),
  "sessionId": z.uuid(),
  "playerId": z.uuid(),
  "isWinner": z.boolean().nullable(),
  "score": z.string().nullable(),
});

export const newSessionParticipantSchema = z.object({
  "sessionId": z.uuid(),
  "playerId": z.uuid(),
  "isWinner": z.boolean().nullable().optional(),
  "score": z.string().nullable().optional(),
});

export const userGameTeachingSchema = z.object({
  "id": z.uuid(),
  "userId": z.uuid(),
  "gameId": z.uuid(),
  "canTeach": z.boolean(),
  "typicalTeachMinutes": z.number().min(-2147483648).max(2147483647).nullable(),
  "createdAt": z.date(),
  "updatedAt": z.date(),
});

export const newUserGameTeachingSchema = z.object({
  "userId": z.uuid(),
  "gameId": z.uuid(),
  "canTeach": z.boolean().optional(),
  "typicalTeachMinutes": z.number().int().min(1).max(600).optional().nullable().nullable().optional(),
});

export const listSchema = z.object({
  "id": z.uuid(),
  "userId": z.uuid(),
  "slug": z.string(),
  "title": z.string(),
  "description": z.string().nullable(),
  "isPublic": z.boolean(),
  "createdAt": z.date(),
  "updatedAt": z.date(),
});

export const newListSchema = z.object({
  "userId": z.uuid(),
  "slug": z.string().min(1).regex(/^[a-z0-9-]+$/),
  "title": z.string().min(1).max(200),
  "description": z.string().nullable().optional(),
  "isPublic": z.boolean().optional(),
});

export const listEntrySchema = z.object({
  "id": z.uuid(),
  "listId": z.uuid(),
  "gameId": z.uuid(),
  "position": z.number().min(-2147483648).max(2147483647),
  "note": z.string().nullable(),
});

export const newListEntrySchema = z.object({
  "listId": z.uuid(),
  "gameId": z.uuid(),
  "position": z.number().min(-2147483648).max(2147483647).int().min(0),
  "note": z.string().nullable().optional(),
});

export type User = z.infer<typeof userSchema>;
export type NewUser = z.infer<typeof newUserSchema>;
export type Game = z.infer<typeof gameSchema>;
export type NewGame = z.infer<typeof newGameSchema>;
export type GameProvenance = z.infer<typeof gameProvenanceSchema>;
export type NewGameProvenance = z.infer<typeof newGameProvenanceSchema>;
export type Player = z.infer<typeof playerSchema>;
export type NewPlayer = z.infer<typeof newPlayerSchema>;
export type ShelfEntry = z.infer<typeof shelfEntrySchema>;
export type NewShelfEntry = z.infer<typeof newShelfEntrySchema>;
export type Session = z.infer<typeof sessionSchema>;
export type NewSession = z.infer<typeof newSessionSchema>;
export type SessionParticipant = z.infer<typeof sessionParticipantSchema>;
export type UserGameTeaching = z.infer<typeof userGameTeachingSchema>;
export type NewUserGameTeaching = z.infer<typeof newUserGameTeachingSchema>;
export type List = z.infer<typeof listSchema>;
export type NewList = z.infer<typeof newListSchema>;
export type ListEntry = z.infer<typeof listEntrySchema>;
